import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, ShieldAlert } from "lucide-react";

export const TwilioReadinessCard = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/org/twilio-readiness").then((r) => setData(r.data)).catch(() => {}); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/org/twilio-readiness/check");
      setData(r.data);
      toast[r.data.ready ? "success" : "info"](r.data.ready ? "Texting setup verified" : "Not ready yet — see what's missing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    }
    setBusy(false);
  };

  if (!data) return null;
  const live = data.mode === "company_live";
  return (
    <Card className="bg-slate-900 border-slate-800 p-6 mt-6" data-testid="twilio-readiness-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-orange-400" /> Texting readiness
            <Badge data-testid="readiness-mode-badge" className={live ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}>
              {live ? "Live under your business name" : "Safe testing mode"}
            </Badge>
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Read-only check against Twilio. Your business name and estimate booking by text turn on only when every check passes.
            {data.checked_at && ` Last checked ${new Date(data.checked_at).toLocaleString()}.`}
          </p>
        </div>
        <Button onClick={run} disabled={busy || !canEdit} data-testid="run-readiness-check-btn" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold shrink-0">
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> {busy ? "Checking…" : "Run check"}
        </Button>
      </div>
      {data.checks.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="readiness-never-checked">Not checked yet.</p>
      ) : (
        <ul className="space-y-2">
          {data.checks.map((c) => (
            <li key={c.key} className="flex items-start gap-3 text-sm" data-testid={`readiness-check-${c.key}`}>
              {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />}
              <div>
                <p className="text-slate-200">{c.label}</p>
                {!c.ok && <p className="text-rose-300/90 text-xs mt-0.5">{c.detail}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
